import { FigureSelectionArea, RegionOfInterest } from 'features/fde/regionTypes'
import {
  DEFAULT_CLASSIFICATION_CONFIG,
  IndicationClassificationConfig,
} from 'features/indicationTriage/classification/indicationClassifier'
import {
  IndicationDetailCardGroup,
  IndicationDetailCardLeaf,
} from 'features/indicationTriage/useIndicationDetailCards'
import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import sortBy from 'lodash/sortBy'
import { useMemo } from 'react'
import {
  GeckoEntry,
  GeckoMeasurement,
  GeckoMeasurementRange,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

/**
 * The externally-exposed IndicationClassification type.
 * refVal is the value we classified - so typically min- or max-
 * depending on the ordering.
 */
export type IndicationClassificationInfo = {
  classification: IndicationClassification
  /**
   * Reference measurement used only to position the verdict on the thickness
   * histogram gauge. Optional and INDEPENDENT of `classification`: a verdict can
   * exist without a plottable reference (e.g. an FFS outcome), so consumers must
   * treat this as possibly-absent rather than gating the verdict on it.
   */
  refVal?: GeckoMeasurement
}

export type IndicationClassification = string

export interface IndicationDetail {
  min_t: number
  avg_t: number
  measurement_unit: string
  total_ut_readings: number
  // String enum key into DAMAGE_MECHANISM_MAPPING (e.g. 'THINNING', 'PITTING').
  // Previously mistyped as `number`, which never matched the string-keyed map
  // and always rendered 'Unknown'.
  damage_mechanism: string
  comments: string
}

export type IndicationStats = {
  minimum: GeckoMeasurement
  mean: GeckoMeasurement
  maximum: GeckoMeasurement
  std_dev: GeckoMeasurement
  min_coords: readonly [number, number]
  max_coords: readonly [number, number]
}

export type IndicationStatsJson = {
  minimum: number
  mean: number
  maximum: number
  std_dev: number
  min_coords: readonly [number, number]
  max_coords: readonly [number, number]
}

export interface IndicationItem extends Omit<RegionOfInterest, 'selection'> {
  componentId: string | null
  selection?: RegionOfInterest['selection']
  detail?: IndicationDetail
  note?: string
  source?: string
  classificationInfo?: IndicationClassificationInfo | null
  indicationSetId: string
  customInfo?: Record<string, unknown>
}

export enum IndicationModes {
  RiskArea = 'riskArea',
  CML = 'cml',
}

export type IndicationItemWithClassification = IndicationItem & {
  classificationInfo: IndicationClassificationInfo | null
}

export const RD_TYPE_INDICATION = RolodexEntryType.INDICATION
export const RD_TYPE_INDICATION_SET = RolodexEntryType.INDICATION_SET

type MeasurementJson = {
  num: number
  unit: string
}

type MeasurementRangeJson = {
  start: MeasurementJson
  end: MeasurementJson
}

type RegionSelectionJson = {
  x: MeasurementRangeJson
  y: MeasurementRangeJson
}

const DEFAULT_UNIT = GeckoMeasurementUnit.Inches

function selectionToJson(selection: FigureSelectionArea): RegionSelectionJson {
  return {
    x: {
      start: {
        num: selection.x.start.num,
        unit: selection.x.start.unit.id,
      },
      end: {
        num: selection.x.end.num,
        unit: selection.x.end.unit.id,
      },
    },
    y: {
      start: {
        num: selection.y.start.num,
        unit: selection.y.start.unit.id,
      },
      end: {
        num: selection.y.end.num,
        unit: selection.y.end.unit.id,
      },
    },
  }
}

function selectionFromJson(json: RegionSelectionJson): FigureSelectionArea {
  return {
    x: GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(
        json.x.start.num,
        GeckoMeasurementUnit.GetByID(json.x.start.unit) ?? DEFAULT_UNIT
      ),
      GeckoMeasurement.CreateFromValue(
        json.x.end.num,
        GeckoMeasurementUnit.GetByID(json.x.end.unit) ?? DEFAULT_UNIT
      )
    ),
    y: GeckoMeasurementRange.CreateFromStartEnd(
      GeckoMeasurement.CreateFromValue(
        json.y.start.num,
        GeckoMeasurementUnit.GetByID(json.y.start.unit) ?? DEFAULT_UNIT
      ),
      GeckoMeasurement.CreateFromValue(
        json.y.end.num,
        GeckoMeasurementUnit.GetByID(json.y.end.unit) ?? DEFAULT_UNIT
      )
    ),
  }
}

function statsToJson(stats: IndicationStats): IndicationStatsJson {
  return {
    ...stats,
    minimum: stats.minimum.toNumber(GeckoMeasurementUnit.Meters),
    maximum: stats.maximum.toNumber(GeckoMeasurementUnit.Meters),
    std_dev: stats.std_dev.toNumber(GeckoMeasurementUnit.Meters),
    mean: stats.mean.toNumber(GeckoMeasurementUnit.Meters),
  }
}

function statsFromJson(json: IndicationStatsJson): IndicationStats {
  return {
    ...json,
    minimum: GeckoMeasurement.CreateFromValue(
      json.minimum,
      GeckoMeasurementUnit.Meters
    ),
    maximum: GeckoMeasurement.CreateFromValue(
      json.maximum,
      GeckoMeasurementUnit.Meters
    ),
    mean: GeckoMeasurement.CreateFromValue(
      json.mean,
      GeckoMeasurementUnit.Meters
    ),
    std_dev: GeckoMeasurement.CreateFromValue(
      json.std_dev,
      GeckoMeasurementUnit.Meters
    ),
  }
}

export type RolodexIndication = IndicationItem & {
  stats?: IndicationStats
  tags: RolodexEntryTagsType & { 'indication-set': string }
}

export function indicationToEntry(
  indication: RolodexIndication
): RolodexEntryJson {
  return {
    id: indication.id,
    type: RD_TYPE_INDICATION,
    data: {
      title: indication.title,
      selection: indication.selection
        ? selectionToJson(indication.selection)
        : undefined,
      detail: indication.detail,
      note: indication.note,
      source: indication.source,
      stats: indication.stats ? statsToJson(indication.stats) : undefined,
      custom_info: indication.customInfo,
    },
    tags: indication.tags,
    index: {},
    updated_at: new Date(Date.now()).toISOString(),
    generation: 1,
    access_restriction: null,
  }
}

export type IndicationSetType =
  | 'raut_report'
  | 'weld_report'
  | 'trilat_report'
  | 'damage_annotation'
  | 'emat_report'

export interface IndicationSet {
  id: string
  title: string
  detailCards: ReadonlyArray<
    IndicationDetailCardLeaf | IndicationDetailCardGroup
  >
  classificationConfig: ReadonlyArray<IndicationClassificationConfig>
  allowCreateOrChangeRegion?: boolean
  indicationSetType?: IndicationSetType
  sortOrder: number
  tags: RolodexEntryTagsType
}

function toArray<T>(item: T | T[]): T[] {
  return Array.isArray(item) ? item : [item]
}

export function entryToIndicationSet(entry: GeckoEntry): IndicationSet {
  return {
    id: entry.id,
    title: entry.data['title'] as string,
    detailCards: entry.data['detailCards'] as IndicationSet['detailCards'],
    classificationConfig: toArray(
      (entry.data['classificationConfig'] ?? []) as
        | IndicationClassificationConfig
        | IndicationClassificationConfig[]
    ).map((config) => ({
      ...config,
      classifications:
        config.classifications ?? DEFAULT_CLASSIFICATION_CONFIG.classifications,
    })),
    indicationSetType: entry.data['indication_set_type'] as
      | IndicationSetType
      | undefined,
    allowCreateOrChangeRegion: entry.data[
      'allowCreateOrChangeRegion'
    ] as IndicationSet['allowCreateOrChangeRegion'],
    sortOrder: (entry.data['sortOrder'] as number) ?? Number.MAX_VALUE,
    tags: entry.tags,
  }
}

export const EMPTY_INDICATION_SET: IndicationSet = {
  id: 'EMPTY',
  title: 'Default Indication Set',
  classificationConfig: [],
  detailCards: [],
  sortOrder: Number.MAX_VALUE,
  tags: {},
}

export function useIndicationSets(
  tags: RolodexEntryQueryBody['tags'] | undefined,
  inspectionId: string | undefined,
  requireInspection?: boolean
): {
  isLoading: boolean
  indicationSets: ReadonlyArray<IndicationSet>
} {
  const { isLoading, entries } = useRolodexEntries(
    useMemo(() => ({ type: RD_TYPE_INDICATION_SET, tags }), [tags]),
    useMemo(
      () => ({
        allowCache: false,
      }),
      []
    )
  )

  return useMemo(
    () => ({
      indicationSets: sortBy(
        (entries ?? [])
          // If we were provided an inspection id,
          // filter down to indication-sets which are either from
          // that inspection, or not tagged to any inspection
          .filter(
            (entry) =>
              (!requireInspection &&
                (!inspectionId || !entry.tags.inspection)) ||
              (!!inspectionId && inspectionId === entry.tags.inspection)
          )
          .map((entry) => entryToIndicationSet(entry)),
        (is) => is.sortOrder
      ),
      isLoading,
    }),
    [entries, inspectionId, isLoading, requireInspection]
  )
}

type RolodexClassificationInfo = {
  classification: IndicationClassification
  // Optional at rest: the auto-default flow writes classification-only entries
  // (no reference measurement), so neither of these is guaranteed on disk.
  refVal?: number
  unitId?: string
}

/**
 * Parses a raw Rolodex `classificationInfo` blob into an
 * `IndicationClassificationInfo`. The verdict (`classification`) is a
 * categorical fact and is kept whenever the blob exists; `refVal` (which only
 * drives the histogram gauge) is built strictly when its unit resolves AND a
 * numeric value is present, and is otherwise absent. The two are independent.
 */
function parseClassificationInfo(
  raw: RolodexClassificationInfo | undefined
): IndicationClassificationInfo | undefined {
  if (!raw) return undefined
  const unit = raw.unitId ? GeckoMeasurementUnit.GetByID(raw.unitId) : undefined
  const refVal =
    unit && typeof raw.refVal === 'number'
      ? GeckoMeasurement.CreateFromValue(raw.refVal, unit)
      : undefined
  return { classification: raw.classification, refVal }
}

export function entryToIndication(entry: RolodexEntryJson): RolodexIndication {
  return {
    id: entry.id,
    title: (entry.data.title ?? 'Untitled') as string,
    componentId: entry.tags['component'] as string,
    indicationSetId: entry.tags[RD_TYPE_INDICATION_SET] as string,
    selection: entry.data.selection
      ? selectionFromJson(entry.data.selection as RegionSelectionJson)
      : undefined,
    classificationInfo: parseClassificationInfo(
      entry.data.classificationInfo as RolodexClassificationInfo | undefined
    ),
    detail: entry.data.detail as IndicationDetail,
    stats: entry.data.stats
      ? statsFromJson(entry.data.stats as IndicationStatsJson)
      : undefined,
    note: entry.data.note as string,
    source: entry.data.source as string,
    customInfo: entry.data.custom_info as Record<string, unknown>,
    tags: {
      ...entry.tags,
      'indication-set': entry.tags[RD_TYPE_INDICATION_SET] ?? '',
    },
  }
}
